import React from 'react';
import { Modal, Button, Space, Typography } from 'antd';
import { ExclamationCircleOutlined, PlayCircleOutlined, FileAddOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/zh-tw';

dayjs.extend(relativeTime);

const { Text, Paragraph } = Typography;

/**
 * Mirror of `active_receipt` summary returned by
 * `GET /api/operator/v1/inbounds/:id` (see
 * `app/controllers/api/operator/v1/inbounds_controller.rb#show`).
 * Backend is the source of truth — keep these field names in sync.
 */
export interface ActiveInboundReceipt {
  id: string;
  name: string;
  item_progress: { scanned: number; expected: number };
  created_at: string;
}

export interface InboundReceiptSelectorModalProps {
  open: boolean;
  activeReceipt: ActiveInboundReceipt | null;
  loading?: boolean;
  onResume: () => void;
  onStartNew: () => void;
  onCancel: () => void;
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 8px;
`;

const ReceiptCard = styled.div`
  border: 1px solid #b7eb8f;
  background: #f6ffed;
  border-radius: 6px;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StartNewNote = styled(Paragraph)`
  margin: 0 !important;
  color: #8c8c8c;
  font-size: 12px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
`;

/**
 * Receipt resume-or-new selector for the inbound acceptance flow.
 *
 * Shown when the inbound has an active `:receiving` receipt with `scanned > 0`.
 * Operator either continues the existing receipt or finalizes it (as
 * `pending_putaway`) and creates a fresh empty one.
 *
 * Inbounds have at most ONE active receipt at a time (unlike ReturnOrders
 * which can have several pending receipts), so we render a single summary
 * card + two buttons instead of a radio list.
 */
const InboundReceiptSelectorModal: React.FC<InboundReceiptSelectorModalProps> = ({
  open,
  activeReceipt,
  loading = false,
  onResume,
  onStartNew,
  onCancel,
}) => {
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: '#faad14' }} />
          {t('operator.inbound_acceptance.receipt_selector.title', {
            defaultValue: '此入庫單有進行中的驗收',
          })}
        </Space>
      }
      onCancel={onCancel}
      footer={null}
      destroyOnClose
      maskClosable={!loading}
      closable={!loading}
    >
      <Body>
        <Text>
          {t('operator.inbound_acceptance.receipt_selector.description', {
            defaultValue: '此入庫單已有一張未結案的驗收單，請選擇要接續驗收或開新一張：',
          })}
        </Text>

        {activeReceipt && (
          <ReceiptCard>
            <Text strong>{activeReceipt.name}</Text>
            <Text type="secondary">
              {t('operator.inbound_acceptance.receipt_selector.progress_summary', {
                defaultValue: '已掃 {{scanned}}/{{expected}} 件 · 開始於 {{since}}',
                scanned: activeReceipt.item_progress?.scanned ?? 0,
                expected: activeReceipt.item_progress?.expected ?? 0,
                since: dayjs(activeReceipt.created_at).fromNow(),
              })}
            </Text>
          </ReceiptCard>
        )}

        <StartNewNote>
          {t('operator.inbound_acceptance.receipt_selector.start_new_note', {
            defaultValue:
              '若選擇「開新一張」：舊驗收單會以已掃品項定案（pending_putaway），並新建一張空白驗收單繼續本次入倉作業。',
          })}
        </StartNewNote>

        <Actions>
          <Button onClick={onCancel} disabled={loading}>
            {t('common.button.cancel', { defaultValue: '取消' })}
          </Button>
          <Button
            danger
            icon={<FileAddOutlined />}
            onClick={onStartNew}
            loading={loading}
          >
            {t('operator.inbound_acceptance.receipt_selector.start_new', {
              defaultValue: '開新一張',
            })}
          </Button>
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            onClick={onResume}
            disabled={loading}
          >
            {t('operator.inbound_acceptance.receipt_selector.resume', {
              defaultValue: '繼續驗收',
            })}
          </Button>
        </Actions>
      </Body>
    </Modal>
  );
};

export default InboundReceiptSelectorModal;
